import {
  AlertCircle,
  Check,
  Info,
  Loader2,
  Paperclip,
  Trash2,
  X,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { toast } from "react-toastify";

import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/tooltip-card";
import {
  blueprintsApi,
  getBlueprintApiErrorMessage,
  type BlueprintDocument,
} from "@/services/blueprints-service";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
interface LocalAttachment {
  id: string;
  file: File;
  status: "pending" | "uploading" | "uploaded" | "error";
}

interface BlueprintAttachmentsProps {
  blueprintId?: string;
  resetKey?: number;
  onFilesChange?: (files: File[]) => void;
  onUpload?: (file: File) => Promise<void>;
  onUploadingChange?: (uploading: boolean) => void;
  instructions?: string;
  onInstructionsChange?: (instructions: string) => void;
}

const MAX_UPLOAD_FILES = 5;
const MAX_UPLOAD_FILE_SIZE = 20 * 1024 * 1024;
const ACCEPTED_UPLOAD_TYPES = ".txt,.md";
const ACCEPTED_EXTENSIONS = [".txt", ".md"];

const FILE_BLOCK_HEADER = "Important; When to read files:";
// Both "when" and its "<CONDITION>" text are optional, so a line stays recognized
// whether the user clears just the condition or the whole "when ..." suffix.
const FILE_BLOCK_LINE_PATTERN = /^Use \{(.+)\}(?:\s+when(?:\s+(.+))?)?\s*$/;
// Matches a file name referenced anywhere in curly braces, e.g. "always use {file.txt}".
const FILE_MENTION_PATTERN = /\{([^{}]+)\}/g;

// A file is already covered if it's referenced anywhere as {name}, even outside the
// strict "Use {name} when <CONDITION>" line format the user may have rewritten it into.
const getMentionedFileNames = (instructions: string): Set<string> => {
  const mentioned = new Set<string>();
  for (const match of instructions.matchAll(FILE_MENTION_PATTERN)) {
    mentioned.add(match[1]);
  }
  return mentioned;
};

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Removed files may be mentioned outside the "Use {file} when" block too (e.g. inline
// in a sentence), so strip those {name} tokens wherever they appear and tidy up the
// leftover whitespace they leave behind.
const stripRemovedFileMentions = (
  text: string,
  removedFileNames: Set<string>,
): string => {
  if (!removedFileNames.size) return text;

  let next = text;
  for (const name of removedFileNames) {
    next = next.replace(new RegExp(`\\{${escapeRegExp(name)}\\}`, "g"), "");
  }

  return next
    .split("\n")
    .map((line) => line.replace(/[ \t]{2,}/g, " ").replace(/[ \t]+$/, ""))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n");
};

const updateInstructionsWithFileBlock = (
  instructions: string,
  previousFileNames: string[],
  nextFileNames: string[],
): string => {
  const addedFileNames = nextFileNames.filter(
    (name) => !previousFileNames.includes(name),
  );
  const removedFileNames = new Set(
    previousFileNames.filter((name) => !nextFileNames.includes(name)),
  );

  if (!addedFileNames.length && !removedFileNames.size) return instructions;

  const mentionedFileNames = getMentionedFileNames(instructions);
  const addedFileNamesNeedingDefault = addedFileNames.filter(
    (name) => !mentionedFileNames.has(name),
  );

  const lines = instructions.split("\n");
  const headerIndex = lines.findIndex(
    (line) => line.trim() === FILE_BLOCK_HEADER,
  );

  let blockEndIndex = headerIndex + 1;
  const fileBlockLines: string[] = [];
  const fileNamesInBlock = new Set<string>();
  if (headerIndex !== -1) {
    // Blank lines and user-entered notes don't end the block; the block extends
    // through the last recognized "Use {file}" line found anywhere below the header.
    let lastMatchIndex = -1;
    for (let i = headerIndex + 1; i < lines.length; i += 1) {
      if (lines[i].match(FILE_BLOCK_LINE_PATTERN)) lastMatchIndex = i;
    }

    blockEndIndex =
      lastMatchIndex === -1 ? headerIndex + 1 : lastMatchIndex + 1;
    for (let i = headerIndex + 1; i < blockEndIndex; i += 1) {
      const match = lines[i].match(FILE_BLOCK_LINE_PATTERN);
      if (match) {
        if (
          !removedFileNames.has(match[1]) &&
          !fileNamesInBlock.has(match[1])
        ) {
          fileBlockLines.push(lines[i]);
          fileNamesInBlock.add(match[1]);
        }
      } else {
        fileBlockLines.push(lines[i]);
      }
    }
  }

  if (headerIndex === -1) {
    const strippedInstructions = stripRemovedFileMentions(
      instructions,
      removedFileNames,
    );
    if (!addedFileNamesNeedingDefault.length) return strippedInstructions;

    const addedBlock = [
      FILE_BLOCK_HEADER,
      ...addedFileNamesNeedingDefault.map(
        (name) => `Use {${name}} when <CONDITION>`,
      ),
    ].join("\n");

    return strippedInstructions
      ? `${strippedInstructions}\n\n${addedBlock}`
      : addedBlock;
  }

  fileBlockLines.push(
    ...addedFileNamesNeedingDefault
      .filter((name) => !fileNamesInBlock.has(name))
      .map((name) => `Use {${name}} when <CONDITION>`),
  );

  const before = stripRemovedFileMentions(
    lines.slice(0, headerIndex).join("\n").replace(/\s+$/, ""),
    removedFileNames,
  );
  const after = stripRemovedFileMentions(
    lines.slice(blockEndIndex).join("\n").replace(/^\s+/, ""),
    removedFileNames,
  );

  if (!fileBlockLines.length) {
    return [before, after].filter(Boolean).join("\n\n");
  }

  return [before, [FILE_BLOCK_HEADER, ...fileBlockLines].join("\n"), after]
    .filter(Boolean)
    .join("\n\n");
};

const getFileExtension = (fileName: string) => {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex >= 0 ? fileName.slice(dotIndex).toLowerCase() : "";
};

// Extension-based check only: MIME types for .txt/.md are unreliable across OSes/browsers.
const isAcceptedFileType = (file: File) =>
  ACCEPTED_EXTENSIONS.includes(getFileExtension(file.name));
const BlueprintAttachments = ({
  blueprintId,
  resetKey,
  onFilesChange,
  onUpload,
  onUploadingChange,
  instructions,
  onInstructionsChange,
}: BlueprintAttachmentsProps) => {
  const { t } = useTranslation();

  const [attachments, setAttachments] = useState<LocalAttachment[]>([]);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (resetKey === undefined) return;
    setAttachments([]);
    setFileInputKey((current) => current + 1);
    onFilesChange?.([]);
  }, [resetKey, onFilesChange]);
  const {
    data: blueprintDocuments = [],
    isLoading: documentsLoading,
    refetch: refetchDocuments,
  } = useQuery<BlueprintDocument[]>({
    queryKey: ["blueprint-documents", blueprintId],
    queryFn: async () => {
      try {
        return await blueprintsApi.getBlueprintDocuments(blueprintId!);
      } catch (error) {
        toast.error(
          getBlueprintApiErrorMessage(error, "Failed to load documents"),
        );
        throw error;
      }
    },
    enabled: Boolean(blueprintId),
    retry: false,
  });

  const documentNames = blueprintDocuments.map(
    (document) =>
      document.name ||
      document.filename ||
      document.file_name ||
      document.title ||
      document.id,
  );
  const attachmentNames = attachments
    .filter(({ status }) => !blueprintId || status !== "uploaded")
    .map(({ file }) => file.name);
  const fileNames = [...documentNames, ...attachmentNames];
  const fileNamesKey = fileNames.join("|");

  const instructionsRef = useRef(instructions);
  useEffect(() => {
    instructionsRef.current = instructions;
  }, [instructions]);

  const onInstructionsChangeRef = useRef(onInstructionsChange);
  useEffect(() => {
    onInstructionsChangeRef.current = onInstructionsChange;
  }, [onInstructionsChange]);

  const fileNamesRef = useRef<string[]>([]);
  const fileNamesInitializedRef = useRef(false);
  useEffect(() => {
    // Wait for existing documents to finish loading before establishing the
    // baseline, otherwise they'd be treated as newly added once they arrive.
    if (blueprintId && documentsLoading) return;

    if (!fileNamesInitializedRef.current) {
      fileNamesRef.current = fileNames;
      fileNamesInitializedRef.current = true;
      return;
    }

    const previousFileNames = fileNamesRef.current;
    const next = updateInstructionsWithFileBlock(
      instructionsRef.current || "",
      previousFileNames,
      fileNames,
    );
    if (next !== instructionsRef.current && onInstructionsChangeRef.current) {
      onInstructionsChangeRef.current(next);
    }
    fileNamesRef.current = fileNames;
    // Update only when the attachment set changes, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileNamesKey, blueprintId, documentsLoading]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (!selectedFiles.length) return;

    const remainingSlots = MAX_UPLOAD_FILES - attachments.length;
    if (selectedFiles.length > remainingSlots) {
      toast.error(t("playground.fileUploadLimitExceeded"));
    }

    const nextAttachments: LocalAttachment[] = [];
    selectedFiles.slice(0, remainingSlots).forEach((file) => {
      const alreadyUploaded = documentNames.some(
        (documentName) =>
          documentName.trim().toLowerCase() === file.name.trim().toLowerCase(),
      );
      const duplicate = [...attachments, ...nextAttachments].some(
        ({ file: existingFile }) =>
          existingFile.name.trim().toLowerCase() ===
            file.name.trim().toLowerCase() &&
          existingFile.size === file.size &&
          existingFile.type.toLowerCase() === file.type.toLowerCase(),
      );

      if (alreadyUploaded || duplicate) {
        toast.error(
          t("playground.duplicateFileUpload", { fileName: file.name }),
        );
        return;
      }
      if (file.size > MAX_UPLOAD_FILE_SIZE) {
        toast.error(t("playground.fileSizeExceedsLimit"));
        return;
      }
      if (!isAcceptedFileType(file)) {
        toast.error(t("playground.unsupportedFileType"));
        return;
      }

      nextAttachments.push({
        id: `${file.name}-${file.size}-${file.lastModified}`,
        file,
        status: blueprintId && onUpload ? "uploading" : "pending",
      });
    });

    setAttachments((current) => {
      const updatedAttachments = [...current, ...nextAttachments];
      if (!blueprintId) {
        onFilesChange?.(
          updatedAttachments
            .filter(({ status }) => status !== "uploaded")
            .map(({ file }) => file),
        );
      }
      return updatedAttachments;
    });
    setFileInputKey((current) => current + 1);

    if (blueprintId && onUpload) {
      onUploadingChange?.(true);
      void Promise.all(
        nextAttachments.map(async ({ id, file }) => {
          try {
            await onUpload(file);
            await refetchDocuments();
            setAttachments((current) =>
              current.filter((attachment) => attachment.id !== id),
            );
          } catch (error) {
            setAttachments((current) =>
              current.map((attachment) =>
                attachment.id === id
                  ? { ...attachment, status: "error" }
                  : attachment,
              ),
            );
            toast.error(
              getBlueprintApiErrorMessage(
                error,
                `Failed to upload ${file.name}`,
              ),
            );
          }
        }),
      ).finally(() => onUploadingChange?.(false));
    }
  };

  const handleRemove = (id: string) => {
    setAttachments((current) => {
      const updatedAttachments = current.filter((item) => item.id !== id);
      if (!blueprintId) {
        onFilesChange?.(
          updatedAttachments
            .filter(({ status }) => status !== "uploaded")
            .map(({ file }) => file),
        );
      }
      return updatedAttachments;
    });
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!blueprintId) return;

    setDeletingDocumentId(documentId);
    try {
      await blueprintsApi.deleteBlueprintDocument(blueprintId, documentId);
      await refetchDocuments();
    } catch (error) {
      toast.error(
        getBlueprintApiErrorMessage(error, "Failed to delete document"),
      );
    } finally {
      setDeletingDocumentId(null);
    }
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-1">
            <Typography
              variant="body2"
              className="text-foreground text-sm font-semibold"
            >
              {t("blueprints.uploadDocuments", "Upload Documents")}
            </Typography>
            <Tooltip
              content={t(
                "blueprints.fields.uploadDocumentsInstructionsTooltip",
                "Uploaded files are parsed into the blueprint's Instructions field to guide the AI. They are not stored as retrievable documents.",
              )}
            >
              <Info className="size-3.5 text-muted-foreground" />
            </Tooltip>
          </div>
          <Typography variant="body2" className="text-sm text-muted-foreground">
            {t(
              "blueprints.fields.uploadDocumentsDescription",
              "Files are used as AI instructions, not retrievable documents.",
            )}
          </Typography>
        </div>

        <label
          htmlFor={`blueprint-file-upload-${fileInputKey}`}
          className="cursor-pointer rounded-[35px] p-2 text-foreground transition-all duration-150 hover:bg-secondary/50"
          title={t(
            "blueprints.fields.uploadDocumentsDescription",
            "Files are used as AI instructions, not retrievable documents.",
          )}
        >
          <Paperclip className="size-5" />
          <span className="sr-only">
            {t(
              "blueprints.fields.uploadDocumentsDescription",
              "Files are used as AI instructions, not retrievable documents.",
            )}
          </span>
          <input
            key={fileInputKey}
            id={`blueprint-file-upload-${fileInputKey}`}
            type="file"
            className="hidden"
            accept={ACCEPTED_UPLOAD_TYPES}
            multiple
            onChange={handleFileChange}
          />
        </label>
      </div>
      <div className="flex min-h-10 w-full flex-wrap gap-2 rounded-[8px] border border-border bg-input p-2">
        {blueprintId && documentsLoading && (
          <span className="flex items-center gap-1 text-sm text-muted-foreground">
            <Loader2 className="size-3 animate-spin" />
            {t("blueprints.loadingDocuments")}
          </span>
        )}
        {blueprintDocuments.map((document) => (
          <span
            key={document.id}
            className="flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-xs text-foreground"
          >
            <span className="max-w-[240px] truncate">
              {document.name ||
                document.filename ||
                document.file_name ||
                document.title ||
                document.id}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-4 rounded-full p-0"
              disabled={deletingDocumentId === document.id}
              onClick={() => void handleDeleteDocument(document.id)}
              aria-label={`Delete ${document.name || document.filename || document.id}`}
            >
              {deletingDocumentId === document.id ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <Trash2 className="size-3" />
              )}
            </Button>
          </span>
        ))}
        {attachments
          .filter(({ status }) => !blueprintId || status !== "uploaded")
          .map(({ id, file, status }) => (
            <span
              key={id}
              className="flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-xs text-foreground"
            >
              {file.name}
              {status === "uploading" && (
                <Loader2
                  className="size-3 animate-spin"
                  aria-label="Uploading"
                />
              )}
              {status === "uploaded" && (
                <Check
                  className="size-3 text-green-600"
                  aria-label="Uploaded"
                />
              )}
              {status === "error" && (
                <AlertCircle
                  className="size-3 text-destructive"
                  aria-label="Upload failed"
                />
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-4 rounded-full p-0"
                onClick={() => handleRemove(id)}
                aria-label={`Remove ${file.name}`}
              >
                <X className="size-3" />
              </Button>
            </span>
          ))}
        {!documentsLoading &&
          !blueprintDocuments.length &&
          !attachments.filter(
            ({ status }) => !blueprintId || status !== "uploaded",
          ).length && (
            <span className="text-sm text-muted-foreground">
              {t("blueprints.noDocumentsSelected")}
            </span>
          )}
      </div>
    </div>
  );
};

export default BlueprintAttachments;
